import { Box, Drawer } from '@mui/material'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import EventNote from '@mui/icons-material/EventNote'
import BarChart from '@mui/icons-material/BarChart'
import Settings from '@mui/icons-material/Settings'
import ClinicBookingsLeftMenu from './ClinicBookingsLeftMenu'

// Width of the desktop left-menu rail (slim icon+label vertical nav). The content column is
// offset by this on md+; mobile/tablet open the same menu in a drawer via a header hamburger.
export const CLINIC_NAV_RAIL_WIDTH = 88

// Fired by the left-menu "Help & Calls" button; the global FloatingIntercomButton listens for it.
export const OPEN_INTERCOM_EVENT = 'aumhum:open-intercom'

// The four clinic-portal sections. Shared by every route (bookings / manage-bookings / stats /
// preferences) so the menu is identical everywhere. Stats is owner-only. `id` mirrors the active
// tab derived from the pathname, so the current item highlights automatically.
export function buildClinicNavItems(roleFlags = {}) {
  return [
    { id: 'bookings', title: 'Check-In', description: 'Clinic check-in & bookings', icon: CalendarMonth },
    { id: 'manage-bookings', title: 'Bookings', description: 'Manage bookings', icon: EventNote },
    ...(roleFlags?.isClinicOwner
      ? [{ id: 'stats', title: 'Stats', description: 'Clinic stats', icon: BarChart }]
      : []),
    { id: 'preferences', title: 'Settings', description: 'Preferences & settings', icon: Settings },
  ]
}

/**
 * Renders the clinic-portal navigation in both its forms:
 *  - a fixed slim rail on desktop (md+), pinned just below the header (`headerHeight`)
 *  - a left drawer on mobile/tablet, opened by the header hamburger (`navOpen`)
 *
 * Both use the same ClinicBookingsLeftMenu + items, so the menu is pixel-identical across routes.
 * The page owns `navOpen`/`headerHeight` and the content-column offset (ml: CLINIC_NAV_RAIL_WIDTH).
 */
export default function ClinicPortalNav({
  items = [],
  currentTab,
  navOpen = false,
  onCloseDrawer,
  headerHeight = 72,
  onSelect,
  onOpenIntercom,
}) {
  return (
    <>
      <Box
        component="aside"
        aria-label="Clinic sections"
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'fixed',
          top: `${headerHeight}px`,
          left: 0,
          width: `${CLINIC_NAV_RAIL_WIDTH}px`,
          height: `calc(100vh - ${headerHeight}px)`,
          overflowY: 'auto',
          padding: '12px 6px',
          borderRight: '1px solid rgba(16, 185, 129, 0.12)',
          backgroundColor: 'rgba(248, 250, 252, 0.6)',
          zIndex: 12,
        }}
      >
        <ClinicBookingsLeftMenu
          items={items}
          selectedId={currentTab}
          onSelect={onSelect}
          onOpenIntercom={onOpenIntercom}
        />
      </Box>

      <Drawer
        anchor="left"
        open={navOpen}
        onClose={onCloseDrawer}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 136,
            padding: '12px 8px',
            backgroundColor: 'rgba(248, 250, 252, 0.98)',
            borderRight: '1px solid rgba(16, 185, 129, 0.12)',
          },
        }}
      >
        <ClinicBookingsLeftMenu
          items={items}
          selectedId={currentTab}
          onSelect={onSelect}
          onOpenIntercom={onOpenIntercom}
        />
      </Drawer>
    </>
  )
}
